import type { NextSeoProps } from "next-seo";
import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ArticleSeo } from "~/components/article/article-seo";
import { AuthorSeo } from "~/components/author/author-seo";
import { PageSeo } from "~/components/meta";
import { TopicSeo } from "~/components/topic/topic-seo";
import type { plusHomeSeoNode$key } from "~/queries/__generated__/plusHomeSeoNode.graphql";

import PlusLogo from "./plus-logo.png";

type Props = NextSeoProps & {
  reference: Nullish<plusHomeSeoNode$key>;
};

export const PlusHomeSeo: FunctionComponent<Props> = ({
  reference: reference_,
  title = "SCMP Plus",
  ...extraSeoProps
}) => {
  const node = useFragment(
    graphql`
      fragment plusHomeSeoNode on Node {
        __typename
        ... on Article {
          ...articleSeoArticle
        }
        ... on Topic {
          ...topicSeoTopic
        }
        ... on Author {
          ...authorSeoAuthor
        }
      }
    `,
    reference_ ?? null,
  );

  switch (node?.__typename) {
    case "Article":
      return (
        <ArticleSeo
          extraSeoOptions={{ ...extraSeoProps, nofollow: true, noindex: true }}
          reference={node}
        />
      );
    case "Author":
      return (
        <AuthorSeo
          extraSeoOptions={{ ...extraSeoProps, nofollow: true, noindex: true }}
          reference={node}
        />
      );
    case "Topic":
      return (
        <TopicSeo
          extraSeoOptions={{ ...extraSeoProps, nofollow: true, noindex: true }}
          reference={node}
        />
      );
    default:
      return (
        <PageSeo
          {...extraSeoProps}
          additionalMetaTags={[
            {
              content:
                "SCMP subscription, premium news subscription, China news, China analysis, China fact sheets, China economy, China politics, China database, China policies, China society, ad free",
              name: "keywords",
            },
            {
              content:
                "SCMP subscription, premium news subscription, China news, China analysis, China fact sheets, China economy, China politics, China database, China policies, China society, ad free",
              name: "news_keywords",
            },
          ]}
          description="SCMP Plus is a premium news platform that helps you decode news on China with curated analysis, summaries, fact sheets, opinion and more. Make informed decisions with this go-to source for business leaders and China watchers seeking comprehensive insights about China’s policies, society and economy."
          nofollow={true}
          noindex={true}
          openGraph={{
            images: [{ url: PlusLogo.src }],
          }}
          title={title}
        />
      );
  }
};

PlusHomeSeo.displayName = "PlusHomeSeo";
